import React, { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Send, Sparkles, MessageSquare, Lightbulb, Zap } from 'lucide-react'

const BACKEND_URL = 'https://deepnx-simple-backend.speedofmastry.workers.dev'

const ChatInterface = ({ onPlanComplete }) => {
    const [messages, setMessages] = useState([
        {
            id: 1,
            type: 'ai',
            content: "👋 Hi! I'm your AI assistant. Describe the app you want to build, and I'll create an enhanced development plan with our 29 specialized pipelines!",
            timestamp: new Date()
        }
    ])
    const [input, setInput] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [showExamples, setShowExamples] = useState(true)
    const messagesEndRef = useRef(null)

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
    }

    useEffect(() => {
        scrollToBottom()
    }, [messages])

    const examplePrompts = [
        {
            icon: <Lightbulb className="w-5 h-5" />,
            text: "Build an e-commerce platform with AI recommendations",
            description: "Full shopping experience with smart product suggestions"
        },
        {
            icon: <MessageSquare className="w-5 h-5" />,
            text: "Create a social media dashboard with real-time analytics",
            description: "Track engagement across multiple platforms"
        },
        {
            icon: <Zap className="w-5 h-5" />,
            text: "Design a project management tool with team collaboration",
            description: "Organize tasks with live team communication"
        }
    ]

    const handleSendMessage = async () => {
        if (!input.trim()) return

        const userMessage = {
            id: Date.now(),
            type: 'user',
            content: input.trim(),
            timestamp: new Date()
        }

        setMessages(prev => [...prev, userMessage])
        setInput('')
        setIsLoading(true)
        setShowExamples(false)

        try {
            // Call AI enhancement endpoint
            const response = await fetch(`${BACKEND_URL}/api/ai/enhance-plan`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    user_input: userMessage.content,
                    enhance_level: 'comprehensive'
                })
            })

            const result = await response.json()

            const aiMessage = {
                id: Date.now() + 1,
                type: 'ai',
                content: result.enhanced_description || `Great! I'll help you build: "${userMessage.content}"\n\nLet me create an enhanced development plan with our AI pipelines...`,
                timestamp: new Date(),
                planData: result
            }

            setMessages(prev => [...prev, aiMessage])

            // Auto-proceed to pipeline after showing the plan
            setTimeout(() => {
                onPlanComplete({
                    original_request: userMessage.content,
                    enhanced_plan: result,
                    user_message: userMessage,
                    ai_response: aiMessage
                })
            }, 2000)

        } catch (error) {
            console.error('Chat error:', error)

            // Fallback AI response
            const fallbackResponse = {
                enhanced_description: `I'll help you build "${userMessage.content}"!\n\nHere's what I'll create:\n• Modern, responsive frontend\n• Secure backend API\n• Database design\n• User authentication\n• Real-time features\n• AI enhancements\n\nLet's start building!`,
                pipelines_selected: ['frontend-generator', 'backend-api', 'database-design', 'auth-system', 'ai-integration'],
                technical_specs: {
                    frontend: 'React + TypeScript',
                    backend: 'Node.js + Express',
                    database: 'PostgreSQL',
                    deployment: 'Cloudflare Workers'
                }
            }

            const aiMessage = {
                id: Date.now() + 1,
                type: 'ai',
                content: fallbackResponse.enhanced_description,
                timestamp: new Date(),
                planData: fallbackResponse
            }

            setMessages(prev => [...prev, aiMessage])

            setTimeout(() => {
                onPlanComplete({
                    original_request: userMessage.content,
                    enhanced_plan: fallbackResponse,
                    user_message: userMessage,
                    ai_response: aiMessage
                })
            }, 2000)
        } finally {
            setIsLoading(false)
        }
    }

    const handleExampleClick = (example) => {
        setInput(example.text)
        setShowExamples(false)
    }

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSendMessage()
        }
    }

    return (
        <div className="max-w-4xl mx-auto">
            {/* Hero Section */}
            <motion.div
                className="text-center mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                <h2 className="text-4xl font-bold gradient-text mb-4">
                    Build Any App with AI Pipelines
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                    Describe your idea and watch as 29 specialized pipelines work together to create your complete application
                </p>
            </motion.div>

            {/* Chat Container */}
            <motion.div
                className="glass-effect rounded-2xl overflow-hidden"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
            >
                {/* Messages Area */}
                <div className="h-96 overflow-y-auto p-6 space-y-4">
                    <AnimatePresence>
                        {messages.map((message) => (
                            <motion.div
                                key={message.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3 }}
                                className={`chat-message ${message.type === 'user' ? 'chat-user' : 'chat-ai'
                                    }`}
                            >
                                <div className="flex items-start space-x-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${message.type === 'user'
                                            ? 'bg-primary text-white'
                                            : 'bg-secondary text-white'
                                        }`}>
                                        {message.type === 'user' ? '👤' : '🤖'}
                                    </div>
                                    <div className="flex-1">
                                        <p className="whitespace-pre-wrap text-gray-800">{message.content}</p>
                                        <span className="text-xs text-gray-500 mt-2 block">
                                            {message.timestamp.toLocaleTimeString()}
                                        </span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>

                    {isLoading && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="chat-message chat-ai"
                        >
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 rounded-full bg-secondary text-white flex items-center justify-center">
                                    🤖
                                </div>
                                <div className="flex space-x-2">
                                    <div className="w-2 h-2 bg-secondary rounded-full animate-bounce"></div>
                                    <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                    <div className="w-2 h-2 bg-secondary rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                                </div>
                            </div>
                        </motion.div>
                    )}

                    <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="border-t border-white/20 p-4">
                    <div className="flex space-x-3">
                        <div className="flex-1">
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Describe the app you want to build..."
                                className="w-full p-3 rounded-lg border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none"
                                rows="2"
                                disabled={isLoading}
                            />
                        </div>
                        <motion.button
                            onClick={handleSendMessage}
                            disabled={!input.trim() || isLoading}
                            className={`p-3 rounded-lg transition-all ${input.trim() && !isLoading
                                    ? 'bg-primary text-white hover:shadow-lg'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                }`}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Send className="w-5 h-5" />
                        </motion.button>
                    </div>
                </div>
            </motion.div>

            {/* Example Prompts */}
            <AnimatePresence>
                {showExamples && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="mt-6"
                    >
                        <p className="text-center text-gray-600 mb-4">Or try one of these examples:</p>
                        <div className="grid md:grid-cols-3 gap-4">
                            {examplePrompts.map((example, index) => (
                                <motion.button
                                    key={index}
                                    onClick={() => handleExampleClick(example)}
                                    className="glass-effect p-4 rounded-lg text-left hover:shadow-lg transition-all group"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <div className="flex items-center space-x-3 mb-2">
                                        <div className="text-primary group-hover:text-secondary transition-colors">
                                            {example.icon}
                                        </div>
                                        <h3 className="font-semibold text-gray-800">{example.text}</h3>
                                    </div>
                                    <p className="text-sm text-gray-600">{example.description}</p>
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

export default ChatInterface